import { Button, Card, EmptyState, Table, type Column } from "@helmr/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listTemplates, type ClassTemplate } from "../../api/classes";
import { useServices } from "../../app/services";
import { Failure, RowsSkeleton, useScope, useStaffNames, styles as c } from "./common";
import { TemplateForm } from "./TemplateForm";
import { WEEKDAY_NAMES } from "./time";

export const slotSummary = (t: Pick<ClassTemplate, "slots">) =>
  [...t.slots].sort((a, b) => ((a.weekday + 6) % 7) - ((b.weekday + 6) % 7) || a.start.localeCompare(b.start)).map((s) => `${WEEKDAY_NAMES[s.weekday]} ${s.start}`).join(", ");

export function TemplatesView() {
  const { client } = useServices();
  const sc = useScope();
  const nav = useNavigate();
  const { nameOf } = useStaffNames();
  const [adding, setAdding] = useState(false);
  const q = useQuery({ queryKey: ["classes", "templates", ...sc.key], queryFn: () => listTemplates(client, { branchId: sc.branchId }).then((r) => r.templates), retry: false });
  const canAdd = sc.ctx.has("classes.template_add");
  const cols: Column<ClassTemplate>[] = [
    { key: "name", header: "Class", cell: (t) => <Link to={`/classes/templates/${t.id}`} className={c.trunc} title={t.name} onClick={(e) => e.stopPropagation()}>{t.name}</Link> },
    { key: "trainer", header: "Trainer", cell: (t) => nameOf(t.trainerId, t.trainerName) },
    { key: "slots", header: "When", cell: (t) => <span className={c.trunc} title={slotSummary(t)}>{slotSummary(t)}</span> },
    { key: "cap", header: "Capacity", align: "end", cell: (t) => t.capacity },
    { key: "dur", header: "Minutes", align: "end", cell: (t) => t.durationMinutes },
  ];
  return (
    <div className={c.stack}>
      {canAdd ? <div><Button variant="primary" onClick={() => setAdding(true)}>New template</Button></div> : null}
      <Card aria-label="Class templates">
        {q.isPending ? <RowsSkeleton /> : q.error ? <Failure error={q.error} title="Could not load templates" onRetry={() => void q.refetch()} /> : q.data.length === 0 ? (
          <EmptyState icon="dumbbell" title="No classes yet: add a template" description="A template is a weekly class. Add one and the calendar fills in." action={canAdd ? <Button variant="primary" onClick={() => setAdding(true)}>Add a template</Button> : undefined} />
        ) : (
          <Table label="Templates" columns={cols} rows={q.data} rowKey={(t) => t.id} onRowClick={(t) => nav(`/classes/templates/${t.id}`)} minWidth={560} summary={`${q.data.length} template${q.data.length === 1 ? "" : "s"}`} />
        )}
      </Card>
      {adding ? <TemplateForm open onClose={() => setAdding(false)} /> : null}
    </div>
  );
}
